/**
 * Codec compacto — base-16384, 14 bits por simbolo.
 *
 * Existe para quando o que importa e CABER: 14 bits por caractere contra os
 * 10 do emoji sao 40% menos caracteres, e num texto longo isso e a diferenca
 * entre colar duas mil figurinhas e colar cem.
 *
 * TROCA EXPLICITA, e a UI precisa dize-la: densidade e discricao sao opostas.
 * Um bloco de ideogramas nao passa por brincadeira como o emoji passa — para
 * quem le a conversa de fora, e obviamente OUTRA COISA. O que ele oferece nao
 * e disfarce melhor, e volume menor. Escolher entre os dois e do usuario.
 *
 * POR QUE 14 BITS E NAO 15. Quinze exigiriam 32768 simbolos, e nao existem
 * tantos ideogramas de uso comum: seria preciso emendar Hangul e a Extensao A,
 * cheia de ideogramas raros que viram quadradinho vazio em fonte sem cobertura
 * — chamativo E fragil, porque um cliente que nao renderiza pode normalizar ou
 * cortar. Os 16384 daqui saem todos do bloco CJK Unified Ideographs comum,
 * atribuido desde o Unicode 3.0 e coberto por qualquer fonte CJK. Custa 6% de
 * densidade contra o maximo teorico e nao quebra.
 *
 * Cada simbolo e UM ponto de codigo do BMP: 3 bytes em UTF-8, uma unidade
 * UTF-16, e conta como um caractere em qualquer contador razoavel.
 *
 * SEM separadores aleatorios, ao contrario do emoji e das palavras. La eles
 * quebram o paredao e imitam escrita ocidental; aqui atrapalhariam duas vezes:
 * texto CJK nao usa espaco entre caracteres, entao espacos denunciariam em vez
 * de disfarcar, e cada um deles seria um caractere a mais num codec cuja razao
 * de existir e economizar caractere.
 */
import { fromSymbols, stripLengthPrefix, toSymbols, withLengthPrefix } from './bitstream.js'
import { DisguiseError } from './types.js'

export const COMPACT_BITS = 14
export const COMPACT_BASE = 1 << COMPACT_BITS

/**
 * Primeiro ponto de codigo do bloco CJK Unified Ideographs.
 *
 * O alfabeto e a faixa contigua [BASE, BASE + 16384), que termina em U+8DFF —
 * bem antes de U+9FCC, o ultimo atribuido desde o Unicode 6.1. Sobra margem
 * de proposito: encostar no fim do bloco e apostar em atribuicoes futuras.
 */
export const COMPACT_FIRST_CODE_POINT = 0x4e00
export const COMPACT_LAST_CODE_POINT = COMPACT_FIRST_CODE_POINT + COMPACT_BASE - 1

/**
 * A faixa e contigua, entao o alfabeto e aritmetica e nao tabela: 16384
 * entradas em arquivo seriam meio megabyte de fonte para dizer "some 0x4E00".
 */
const paraSimbolo = (indice: number): string =>
  String.fromCodePoint(COMPACT_FIRST_CODE_POINT + indice)

const paraIndice = (codePoint: number): number | undefined => {
  const indice = codePoint - COMPACT_FIRST_CODE_POINT
  return indice >= 0 && indice < COMPACT_BASE ? indice : undefined
}

export function encodeCompact(payload: Uint8Array): string {
  const symbols = toSymbols(withLengthPrefix(payload), COMPACT_BITS)
  let out = ''
  for (const s of symbols) out += paraSimbolo(s)
  return out
}

export function decodeCompact(text: string): Uint8Array {
  const symbols: number[] = []
  // Por ponto de codigo, nao por unidade UTF-16.
  for (const char of text) {
    if (/\s/u.test(char)) continue
    const indice = paraIndice(char.codePointAt(0) ?? -1)
    if (indice === undefined) {
      throw new DisguiseError(`simbolo fora do alfabeto compacto: ${char}`)
    }
    symbols.push(indice)
  }
  if (symbols.length === 0) throw new DisguiseError('texto compacto vazio')
  return stripLengthPrefix(fromSymbols(symbols, COMPACT_BITS))
}

/** `true` se o texto so tem simbolos da faixa (e espacos). Usado na deteccao. */
export function looksLikeCompact(text: string): boolean {
  let seen = 0
  for (const char of text) {
    if (/\s/u.test(char)) continue
    if (paraIndice(char.codePointAt(0) ?? -1) === undefined) return false
    seen += 1
  }
  return seen > 0
}
